import React, { createContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { UserProfile, ScopePermissions, UserScope, DirectorySession } from '../types/auth.types';
import { getDirectoryMe } from '../services/erliaAuthSession.service';
import { useAuth } from '../hooks/useAuth';
import { defaultPermissions, calculatePermissions } from '../utils/permissions.utils';
import { resolveDirectoryPermission } from '../utils/effectiveAccess.utils';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';

interface UserProfileContextType {
  userProfile: UserProfile | null;
  /** Sesión de producto erlia-auth (GET /me). */
  directorySession: DirectorySession | null;
  permissions: ScopePermissions;
  isLoading: boolean;
  error: string | null;
  directoryError: string | null;
  hasScope: (scope: UserScope) => boolean;
  hasAnyScope: (scopes: UserScope[]) => boolean;
  hasAllScopes: (scopes: UserScope[]) => boolean;
  hasPermission: (code: string) => boolean;
  hasAnyPermission: (codes: string[]) => boolean;
  hasAllPermissions: (codes: string[]) => boolean;
  /** AuthZ de UI: permission de GET /me (con mapeo create:user→manage:users). */
  hasEffectiveAccess: (code: string) => boolean;
  hasAnyEffectiveAccess: (codes: string[]) => boolean;
  hasAllEffectiveAccess: (codes: string[]) => boolean;
  refreshProfile: () => Promise<void>;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

export { UserProfileContext };

export const UserProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [directorySession, setDirectorySession] = useState<DirectorySession | null>(null);
  const [permissions, setPermissions] = useState<ScopePermissions>(defaultPermissions);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [directoryError, setDirectoryError] = useState<string | null>(null);

  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { handleError } = useErrorHandler();

  const isLoadingRef = useRef(false);
  const handleErrorRef = useRef(handleError);

  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  const loadUserProfile = useCallback(async () => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setUserProfile(null);
      setDirectorySession(null);
      setPermissions(defaultPermissions);
      setError(null);
      setDirectoryError(null);
      setIsLoading(false);
      isLoadingRef.current = false;
      return;
    }

    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);
    setDirectoryError(null);

    try {
      const me = await getDirectoryMe();
      setDirectorySession(me);
      setPermissions(calculatePermissions(me.permissions));
      setUserProfile({
        email: me.email,
        scopes: me.permissions,
      });
      setDirectoryError(null);
    } catch (err) {
      setDirectorySession(null);
      setUserProfile(null);
      setPermissions(defaultPermissions);
      const message =
        err instanceof Error
          ? err.message
          : 'No se pudo cargar la sesión del directorio';
      setDirectoryError(message);
      setError(message);
      handleErrorRef.current(err, { showNotification: false, logToConsole: true });
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [isAuthenticated, authLoading]);

  const hasScope = (scope: UserScope): boolean => {
    return userProfile?.scopes.includes(scope) ?? false;
  };

  const hasAnyScope = (scopes: UserScope[]): boolean => {
    return scopes.some((scope) => hasScope(scope));
  };

  const hasAllScopes = (scopes: UserScope[]): boolean => {
    return scopes.every((scope) => hasScope(scope));
  };

  const hasPermission = (code: string): boolean => {
    return directorySession?.permissions.includes(code) ?? false;
  };

  const hasAnyPermission = (codes: string[]): boolean => {
    return codes.some((code) => hasPermission(code));
  };

  const hasAllPermissions = (codes: string[]): boolean => {
    return codes.every((code) => hasPermission(code));
  };

  const hasEffectiveAccess = (code: string): boolean => {
    if (isLoading && !directorySession) {
      return false;
    }

    return hasPermission(resolveDirectoryPermission(code));
  };

  const hasAnyEffectiveAccess = (codes: string[]): boolean =>
    codes.some((code) => hasEffectiveAccess(code));

  const hasAllEffectiveAccess = (codes: string[]): boolean =>
    codes.every((code) => hasEffectiveAccess(code));

  const refreshProfile = useCallback(async () => {
    isLoadingRef.current = false;
    await loadUserProfile();
  }, [loadUserProfile]);

  useEffect(() => {
    if (!authLoading) {
      loadUserProfile();
    }
  }, [isAuthenticated, authLoading, loadUserProfile]);

  const value: UserProfileContextType = {
    userProfile,
    directorySession,
    permissions,
    isLoading,
    error,
    directoryError,
    hasScope,
    hasAnyScope,
    hasAllScopes,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasEffectiveAccess,
    hasAnyEffectiveAccess,
    hasAllEffectiveAccess,
    refreshProfile,
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};
